import { useState, useEffect, useMemo } from 'react'
import { useSurgeryItemsQuery } from 'lib/apollo/hooks'
import { ItemResponse } from 'common/types'
import { extractTrayJSONData } from 'lib/utils/extractTrayJSONData'
import { useApolloClient } from '@apollo/client'

interface UseSurgeryTraysReturn {
  trays: ItemResponse[]
  loading: boolean
  error: Error | null
  filteredTrays: ItemResponse[]
  expanded: boolean
  toggleExpanded: () => void
  updateTrays: (updatedTrays: ItemResponse[]) => void
  refetchTrays: () => void
}

/**
 * Custom hook to manage surgery trays data and state
 * Follows clean code principles with clear separation of concerns
 */
export const useSurgeryTrays = (surgeryId: string): UseSurgeryTraysReturn => {
  const [expanded, setExpanded] = useState(false)
  const [localTrays, setLocalTrays] = useState<ItemResponse[]>([])
  const { loading, error, surgeryItems } = useSurgeryItemsQuery(surgeryId)
  const client = useApolloClient()

  // Update local trays when surgeryItems changes
  useEffect(() => {
    if (surgeryItems) {
      setLocalTrays(surgeryItems)
    }
  }, [surgeryItems])

  // Filter trays to only show approved/relevant ones
  const filteredTrays = useMemo(() => {
    if (!localTrays || localTrays.length === 0) {
      return []
    }

    return localTrays.filter((surgeryTray: ItemResponse) => {
      const trayProductDetails = extractTrayJSONData(surgeryTray)

      // If tray is consigned or loaned, it needs to be approved before showing it to the user
      if (trayProductDetails?.trayCategory !== 'facility') {
        return (
          trayProductDetails?.status !== 'rejected' &&
          trayProductDetails?.status !== undefined
        )
      }

      return true
    })
  }, [localTrays])

  const toggleExpanded = () => {
    setExpanded((prev) => !prev)
  }

  const updateTrays = (updatedTrays: ItemResponse[]) => {
    setLocalTrays(updatedTrays)
  }

  const refetchTrays = () => {
    // Evict all surgery tray queries from cache to force refetch
    client.cache.evict({ fieldName: 'getSurgeryItems' })
    client.cache.gc()
  }

  // Auto-expand if there are trays
  useEffect(() => {
    if (filteredTrays.length > 0 && !expanded) {
      setExpanded(true)
    }
  }, [filteredTrays.length, expanded])

  return {
    trays: localTrays,
    loading,
    error: error || null,
    filteredTrays,
    expanded,
    toggleExpanded,
    updateTrays,
    refetchTrays,
  }
}
